import { Box, Button, Checkbox, ChoiceList, Divider, Icon, Layout, Modal, Page, Text, Link } from "@shopify/polaris";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { PolarisCard } from "src/components/shopify/PolarisCard";
import {
  Origin,
  SupplierRegisterInput,
  useRegisterSupplierMutation,
  useResendSupplierOtpMutation,
  useVerifySupplierOtpMutation,
} from "src/graphql/codegen_graphql";
import TextField from "src/components/shopify/form/TextField";
import { PhoneMajor, PasskeyMajor, EmailMajor, LockMinor, StoreMinor } from "@shopify/polaris-icons";
import { Toast } from "src/hooks/toast";
import SellerLocation from "./SellerLoacation";
import useWindowDimensions from "src/hooks/useWindow";

interface State {
  email: string;
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
  inventoryLocation: string;
}

interface InputInfo extends SupplierRegisterInput {
  confirmPassword: string;
  passkey: string;
  code: string;
  issend: boolean;
  isSeller: boolean;
  term: boolean;
  fees: boolean;
}

export function RegisterScreen() {
  const { width } = useWindowDimensions();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<InputInfo>({
    origin: Origin.Local,
    password: "",
    username: "",
    confirmPassword: "",
    passkey: "",
    code: "",
    issend: false,
    isSeller: false,
    term: false,
    fees: false,
    coordLat: "11.5762787",
    coordLng: "104.9226139",
  });
  const [error, setError] = useState<{ msg: string; type: "USERNAME" | "PASSWORD" | "ADDRESS" | undefined }>({
    msg: "",
    type: undefined,
  });
  const [registerSeller, { loading }] = useRegisterSupplierMutation();

  const handleRegisterToVerify = useCallback(() => {
    if (!info.username) {
      setError({
        msg: info.origin === Origin.Local ? "Phone number was required." : "Email was required.",
        type: "USERNAME",
      });
      return;
    }
    if (!info.password) {
      setError({
        msg: "Password was required.",
        type: "PASSWORD",
      });
      return;
    }
    if (!info.confirmPassword) {
      setError({
        msg: "Confirm password was required.",
        type: "PASSWORD",
      });
      return;
    }
    if (info.password !== info.confirmPassword) {
      setError({
        msg: "Password and confirm password not match",
        type: "PASSWORD",
      });
      return;
    }

    if (!info.term) {
      return Toast.onShow("Please agree term conditions.", "danger", "right");
    }

    if (info.origin === Origin.Local && !info.address) {
      Toast.onShow("Please enter your address location.", "danger", "right");
      setError({
        msg: "Please enter your address location.",
        type: "ADDRESS",
      });
      return;
    }

    const data = {
      origin: info.origin,
      username: info.username,
      password: info.password,
      companyName: info.companyName,
      address: info.address,
      coordLat: String(info.coordLat),
      coordLng: String(info.coordLng),
    };

    registerSeller({
      variables: {
        data,
      },
    }).then((res) => {
      if (!!res.data?.registerSupplier?.success) {
        Toast.onShow(res.data.registerSupplier.message || "", "success", "right");
        setInfo({
          ...info,
          passkey: res.data.registerSupplier.token || "",
          issend: true,
        });
        setTimeout(() => {
          setOpen(true);
        }, 500);
      } else {
        Toast.onShow(
          res.data?.registerSupplier?.message?.replace(
            "username",
            info.origin === Origin.Local ? "Phone number" : "Email",
          ) || "",
          "danger",
          "right",
        );
      }
    });
  }, [info, registerSeller]);

  return (
    <Page title="Register Seller" titleHidden>
      <ModalVerifyOtp
        passKey={info.passkey}
        isSeller={info.isSeller}
        open={open}
        onClose={() => {}}
        onChangeKey={(key) => setInfo({ ...info, passkey: key })}
      />
      <Layout>
        <Layout.Section variant="oneThird">
          <div style={{ position: "relative" }}>
            <div
              style={{
                width: width > 600 ? 600 : width,
                position: "absolute",
                left: "50%",
                transform: "translate(-50%,0)",
              }}
            >
              <PolarisCard>
                <Box padding={"0"}>
                  <Text as="h3" variant="headingXl" alignment="center">
                    Register Seller
                  </Text>
                </Box>
                <br />
                <Box padding={"300"}>
                  <div>
                    <label>What is your current origin? ({info.origin === "Local" ? "Cambodia" : info.origin})</label>
                    <ChoiceList
                      choices={[
                        {
                          label: "Cambodia",
                          value: "Local",
                        },
                        {
                          label: "China",
                          value: "China",
                        },
                      ]}
                      selected={[info.origin]}
                      title="What is your current origin?"
                      titleHidden
                      onChange={(v) => {
                        setInfo({
                          ...info,
                          origin: v[0] as Origin,
                        });
                      }}
                    />
                  </div>
                  <br />
                  <Divider borderColor="border-info" borderWidth="025" />
                  <br />
                  <div>
                    <TextField
                      type={info.origin === Origin.Local ? "number" : "text"}
                      label
                      labelHidden
                      prefix={
                        info.origin === Origin.Local ? (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Icon source={PhoneMajor} />
                            <span>(+855)</span>
                          </div>
                        ) : (
                          <Icon source={EmailMajor} />
                        )
                      }
                      autoComplete="off"
                      value={info.username}
                      onChange={(v) =>
                        setInfo({
                          ...info,
                          username: v,
                        })
                      }
                      placeholder={info.origin === Origin.Local ? "Enter your phone number" : "Enter your email"}
                      required
                      error={error.type === "USERNAME" && <small>{error.msg}</small>}
                    />
                    <br />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div style={{ width: width > 600 ? 250 : width / 2 }}>
                        <TextField
                          label
                          labelHidden
                          prefix={<Icon source={PasskeyMajor} />}
                          autoComplete="off"
                          value={info.password}
                          type="password"
                          onChange={(v) =>
                            setInfo({
                              ...info,
                              password: v,
                            })
                          }
                          placeholder="Enter password"
                          required
                          error={error.type === "PASSWORD" && <small>{error.msg}</small>}
                        />
                      </div>
                      <div style={{ width: width > 600 ? 250 : width / 2 }}>
                        <TextField
                          label
                          labelHidden
                          prefix={<Icon source={LockMinor} />}
                          autoComplete="off"
                          value={info.confirmPassword}
                          type="password"
                          onChange={(v) =>
                            setInfo({
                              ...info,
                              confirmPassword: v,
                            })
                          }
                          placeholder="Confirm password"
                          required
                          error={error.type === "PASSWORD" && <small>{error.msg}</small>}
                        />
                      </div>
                    </div>
                  </div>
                  <br />
                  <Divider borderColor="border-info" borderWidth="025" />
                  <br />
                  <div>
                    <TextField
                      value={info.companyName || ""}
                      label
                      labelHidden
                      autoComplete="off"
                      prefix={<Icon source={StoreMinor} />}
                      placeholder="Company Name"
                      onChange={(v) => setInfo({ ...info, companyName: v })}
                    />
                    {info.origin === Origin.Local && (
                      <>
                        <br />
                        <TextField
                          value={info.username || ""}
                          label
                          labelHidden
                          autoComplete="off"
                          prefix={<Icon source={PhoneMajor} />}
                          helpText="This phone number will be your login username."
                          readonly
                          placeholder="Phone Number"
                          disabled
                        />
                      </>
                    )}
                    <br />
                    <div>
                      <div
                        style={{
                          border: "solid 0.04125rem rgb(138,138,138)",
                          borderRadius: "0.5rem",
                          padding: "6px 12px",
                        }}
                      >
                        <SellerLocation
                          lat={Number(info.coordLat || 0)}
                          lng={Number(info.coordLng || 0)}
                          addressName={info.address || ""}
                          onChange={(lat, lng, addressName) => {
                            setInfo({
                              ...info,
                              coordLat: lat.toString(),
                              coordLng: lng.toString(),
                              address: addressName,
                            });
                          }}
                        />
                      </div>
                    </div>
                    {info.origin === Origin.Local && (
                      <>
                        <br />
                        <div
                          style={{
                            border: "solid 0.04125rem rgb(138,138,138)",
                            borderRadius: "0.5rem",
                            padding: "6px 12px",
                          }}
                        >
                          <div style={{ display: "flex" }}>
                            <Checkbox checked label labelHidden tone="magic" />
                            <img
                              src="/assets/delivery_service.png"
                              alt=""
                              style={{ width: 50, height: 50, margin: "0 0.5rem", borderRadius: "0.5rem" }}
                            />
                            <Text as="span" variant="bodyMd">
                              Deliver your packages to your customers at affordable cost and quick cash on delivery
                              settlement.
                            </Text>
                          </div>
                          <br />
                          <div style={{ display: "flex" }}>
                            <Checkbox
                              checked={info.isSeller}
                              label
                              labelHidden
                              tone="magic"
                              onChange={(v) => setInfo({ ...info, isSeller: v })}
                            />
                            <img
                              src="/assets/open_store.png"
                              alt=""
                              style={{ width: 50, height: 50, margin: "0 0.5rem", borderRadius: "0.5rem" }}
                            />
                            <Text as="span" variant="bodyMd">
                              Sell your products on L192 shopping app, the leading shopping in Cambodia.
                            </Text>
                          </div>
                        </div>
                      </>
                    )}
                    <br />
                    <div style={{ display: "flex" }}>
                      <Text as="p" variant="bodyMd" alignment="center">
                        Read{" "}
                        <Link url={"/merchant/agreement"} target="_blank">
                          Terms of Use and Privacy policies
                        </Link>{" "}
                        {/* &{" "}
                        <a href="https://groupincorp.com/docs/l192" target="_blank" rel="noopener noreferrer">
                          Fees and Commissions
                        </a> */}
                        . If you agree please click the checkboxes below.
                      </Text>
                    </div>
                    <br />
                    <div>
                      <div style={{ display: "flex" }} className="mb-2">
                        <Checkbox
                          checked={info.term}
                          onChange={(v) => setInfo({ ...info, term: v })}
                          label
                          labelHidden
                        />
                        <div className="ml-2">
                          <Text as="span" variant="bodyMd">
                            I agree to Terms of Use and Privacy policies
                          </Text>
                        </div>
                      </div>
                      {/* <div style={{ display: "flex" }}>
                        <Checkbox
                          checked={info.fees}
                          onChange={(v) => setInfo({ ...info, fees: v })}
                          label
                          labelHidden
                        />
                        <div className="ml-2">
                          <Text as="span" variant="bodyMd">
                            I agree to Fees and Commission
                          </Text>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </Box>
                <Divider borderColor="border-info" borderWidth="025" />
                <br />
                <Box>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      fullWidth
                      variant="primary"
                      disabled={!info.username || !info.password}
                      tone="success"
                      onClick={handleRegisterToVerify}
                      loading={loading}
                    >
                      Submit
                    </Button>
                  </div>
                </Box>
              </PolarisCard>
            </div>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function ModalVerifyOtp(props: {
  passKey: string;
  isSeller: boolean;
  open: boolean;
  onClose: any;
  onChangeKey: (v: string) => void;
}) {
  const q = new URLSearchParams(window.location.search);
  const history = useHistory();
  const [otpInput, setOtpInput] = useState(["", "", "", ""]);
  const [focus, setFocus] = useState(0);
  const [verifyOtp, { loading: loading1 }] = useVerifySupplierOtpMutation();
  const [resendOtp, { loading: loading2 }] = useResendSupplierOtpMutation();

  const handleChangeInput = useCallback(
    (value: any, index: number) => {
      const v = [...otpInput];
      v[index] = value;
      setOtpInput(v);
      value !== "" && index < v.length && setFocus(index + 1);
      value === "" && index > 0 && setFocus(index - 1);
    },
    [otpInput],
  );

  const handleVerify = useCallback(() => {
    verifyOtp({
      variables: {
        verifyToken: props.passKey,
        secretCode: otpInput.join(""),
        isSeller: true,
        verifyForgeted: false,
      },
    }).then((res) => {
      if (!!res.data?.verifySupplierOtp?.success) {
        if (res.data.verifySupplierOtp.status === "VERIFIED") {
          Toast.onShow(res.data?.verifySupplierOtp?.message || "", "success", "right");
          setTimeout(() => {
            history.push({
              pathname: "/",
            });
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }, 500);
        }
      } else {
        Toast.onShow(res.data?.verifySupplierOtp?.message || "", "danger", "right");
      }
    });
  }, [history, otpInput, props, verifyOtp]);

  const handleResendOtp = useCallback(() => {
    resendOtp({
      variables: {
        verifyToken: props.passKey,
        verifyForgeted: false,
      },
    }).then((res) => {
      if (res.data?.resendSupplierOtp?.success) {
        Toast.onShow(res.data.resendSupplierOtp.message || "", "success", "right");
        props.onChangeKey(res.data.resendSupplierOtp.token || "");
        q.set("passkey", res.data.resendSupplierOtp.token || "");
      }
    });
  }, [q, props, resendOtp]);

  return (
    <Modal title="" titleHidden open={props.open} onClose={props.onClose}>
      <Modal.Section>
        <Box padding={"300"}>
          <Text as="h3" variant="headingXl" alignment="center">
            Enter the code
          </Text>
        </Box>
        <Box padding={"300"}>
          <Text as="p" variant="bodyMd" alignment="center">
            We sent a code to your phone number or email to confirm.
          </Text>
        </Box>
        <Box padding={"300"}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ display: "flex", justifyContent: "space-between", width: 300 }}>
              {[...new Array(4)].map((x, i) => {
                return (
                  <div key={i} style={{ width: 40 }}>
                    <TextField
                      focused={focus === i}
                      onChange={(v) => handleChangeInput(v, i)}
                      label=""
                      align="center"
                      labelHidden
                      value={otpInput[i]}
                      autoComplete="off"
                      maxLength={1}
                      minLength={1}
                      loading={loading1 || loading2}
                      disabled={loading1 || loading2}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </Box>
        <br />
        <Box padding={"300"}>
          <Button
            fullWidth
            tone="success"
            variant="primary"
            loading={loading1 || loading2}
            disabled={loading1 || loading2}
            onClick={handleVerify}
          >
            Verify Account
          </Button>
        </Box>
        <Box padding={"300"}>
          <Text as="p" variant="bodyMd" alignment="center">
            If you not receive code or code was expire. <Link onClick={handleResendOtp}>Resend Code</Link>
          </Text>
        </Box>
      </Modal.Section>
    </Modal>
  );
}
